import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, CheckCircle2, AlertCircle, Download } from "lucide-react";
import { useEffect } from "react";

const Implementation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { tier, assessmentData, strategy } = location.state || {};

  useEffect(() => {
    if (!tier && !strategy) {
      navigate("/");
    }
  }, [tier, strategy, navigate]);

  // Get tier from state or from strategy
  const effectiveTier = tier || strategy?.recommended_tier || "core";

  const handleDownloadJSON = () => {
    if (!strategy) return;
    
    const dataStr = JSON.stringify(strategy, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `gamification-strategy-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (!effectiveTier) {
    return null;
  }

  const implementationGuides = {
    core: {
      technical: [
        "Implement basic progress bars and completion indicators",
        "Set up achievement/goal tracking system",
        "Create simple reward notification system",
        "Design clear visual feedback for actions",
      ],
      design: [
        "Use clear, consistent progress indicators",
        "Implement straightforward color coding for status",
        "Create simple, uncluttered UI elements",
        "Focus on clarity and ease of use",
      ],
      transitions: [
        "Consider Moderate tier when user engagement plateaus",
        "Upgrade if users request social features",
        "Move up when retention metrics stabilize",
      ],
    },
    moderate: {
      technical: [
        "Integrate social sharing and comparison features",
        "Implement achievement badge system",
        "Add personalization engine for content",
        "Set up basic audio feedback system",
        "Create user profile and history tracking",
      ],
      design: [
        "Design engaging achievement badges and icons",
        "Create social comparison widgets",
        "Implement personalized UI themes",
        "Add subtle audio cues for actions",
        "Design progress visualization charts",
      ],
      transitions: [
        "Consider High tier when community forms naturally",
        "Upgrade if users engage heavily with social features",
        "Move up when ready for competitive elements",
      ],
    },
    high: {
      technical: [
        "Build comprehensive leaderboard system",
        "Implement multi-sensory feedback (audio + haptic)",
        "Create advanced analytics dashboard",
        "Set up community features and forums",
        "Develop adaptive difficulty algorithms",
        "Integrate rich notification system",
      ],
      design: [
        "Create immersive multi-sensory experiences",
        "Design competitive leaderboard interfaces",
        "Build sophisticated progress visualizations",
        "Implement dynamic UI that adapts to user",
        "Create rich community interaction spaces",
      ],
      transitions: [
        "Maintain and optimize at this level",
        "Focus on retention and community growth",
        "Consider new features based on analytics",
      ],
    },
  };

  const currentGuide = implementationGuides[effectiveTier as keyof typeof implementationGuides] || implementationGuides.core;

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 py-12 px-4">
      <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div className="flex justify-between items-center">
          <Button
            variant="ghost"
            onClick={() => navigate("/results", { state: { assessmentData, strategy, submitted: true } })}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Results
          </Button>
          
          {strategy && (
            <Button
              variant="outline"
              onClick={handleDownloadJSON}
              className="mb-4"
            >
              <Download className="w-4 h-4 mr-2" />
              Download JSON
            </Button>
          )}
        </div>

        {/* Header */}
        <div className="text-center space-y-4">
          <h1 className="text-4xl md:text-5xl font-bold">Implementation Guide</h1>
          <p className="text-xl text-muted-foreground">
            Technical specifications and design guidelines for your {effectiveTier} tier strategy
          </p>
        </div>

        {/* Technical Specifications */}
        <Card className="p-8 shadow-lg">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold">Technical Specifications</h2>
            </div>
            <ul className="space-y-3 ml-9">
              {currentGuide.technical.map((spec, index) => (
                <li key={index} className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                  <span className="text-muted-foreground">{spec}</span>
                </li>
              ))}
            </ul>
          </div>
        </Card>

        {/* Design Guidelines */}
        <Card className="p-8 shadow-lg">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6 text-accent" />
              <h2 className="text-2xl font-bold">Design Guidelines</h2>
            </div>
            <ul className="space-y-3 ml-9">
              {currentGuide.design.map((guideline, index) => (
                <li key={index} className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-accent mt-2 flex-shrink-0" />
                  <span className="text-muted-foreground">{guideline}</span>
                </li>
              ))}
            </ul>
          </div>
        </Card>

        {/* Transition Guidance */}
        <Card className="p-8 shadow-lg border-2 border-accent/20">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-6 h-6 text-accent" />
              <h2 className="text-2xl font-bold">When to Transition Tiers</h2>
            </div>
            <ul className="space-y-3 ml-9">
              {currentGuide.transitions.map((transition, index) => (
                <li key={index} className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-accent mt-2 flex-shrink-0" />
                  <span className="text-muted-foreground">{transition}</span>
                </li>
              ))}
            </ul>
          </div>
        </Card>

        {/* Best Practices */}
        <Card className="p-8 shadow-lg bg-gradient-to-br from-primary/5 to-accent/5">
          <h2 className="text-2xl font-bold mb-4">Best Practices</h2>
          <div className="space-y-3 text-muted-foreground">
            <p>
              • <strong>Start simple:</strong> Implement core features first, then iterate based on user feedback
            </p>
            <p>
              • <strong>Test extensively:</strong> A/B test different gamification elements to find what resonates
            </p>
            <p>
              • <strong>Monitor metrics:</strong> Track engagement, retention, and user satisfaction continuously
            </p>
            <p>
              • <strong>Stay flexible:</strong> Be ready to adjust your strategy based on real user behavior
            </p>
          </div>
        </Card>

        {/* CTA */}
        <div className="text-center space-y-4">
          <Button
            size="lg"
            onClick={() => navigate("/")}
            className="text-lg px-8 py-6 bg-gradient-to-r from-primary to-accent hover:opacity-90 transition-opacity"
          >
            Start New Assessment
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Implementation;
